import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Alert {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number; // in milliseconds, 0 means persistent
}

interface AlertState {
  alerts: Alert[];
}

const initialState: AlertState = {
  alerts: [],
};

// Helper function to generate unique IDs
const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

const alertSlice = createSlice({
  name: 'alert',
  initialState,
  reducers: {
    showAlert: (state, action: PayloadAction<Omit<Alert, 'id'>>) => {
      const alert: Alert = {
        id: generateId(),
        ...action.payload,
        duration: action.payload.duration ?? 2000, // default 2 seconds
      };
      state.alerts.push(alert);
    },
    
    showSuccess: (state, action: PayloadAction<{ message: string; duration?: number }>) => {
      const alert: Alert = {
        id: generateId(),
        type: 'success',
        message: action.payload.message,
        duration: action.payload.duration ?? 2000,
      };
      state.alerts.push(alert);
    },
    
    showError: (state, action: PayloadAction<{ message: string; duration?: number }>) => {
      const alert: Alert = {
        id: generateId(),
        type: 'error',
        message: action.payload.message,
        duration: action.payload.duration ?? 2000,
      };
      state.alerts.push(alert);
    },
    
    showWarning: (state, action: PayloadAction<{ message: string; duration?: number }>) => {
      const alert: Alert = {
        id: generateId(),
        type: 'warning',
        message: action.payload.message,
        duration: action.payload.duration ?? 2000,
      };
      state.alerts.push(alert);
    },
    
    showInfo: (state, action: PayloadAction<{ message: string; duration?: number }>) => {
      const alert: Alert = {
        id: generateId(),
        type: 'info',
        message: action.payload.message,
        duration: action.payload.duration ?? 5000,
      };
      state.alerts.push(alert);
    },
    
    clearAlert: (state, action: PayloadAction<string>) => {
      state.alerts = state.alerts.filter(alert => alert.id !== action.payload);
    },
    
    clearAllAlerts: (state) => {
      state.alerts = [];
    },
  },
});

export const {
  showAlert,
  showSuccess,
  showError,
  showWarning,
  showInfo,
  clearAlert,
  clearAllAlerts,
} = alertSlice.actions;

export default alertSlice.reducer;